import jsonLogic, { RulesLogic } from 'json-logic-js';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { UiStatus } from 'inquiry/redux/actions/ui';
import { UiComponentRule } from 'lib/interfaces/ui-components';
import { captureException } from 'lib/reporting';
import { FormValue, FormValues, Rule } from 'lib/utils/formValidation';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { RootState } from 'verify/redux/store';

export type ComponentClientJsonLogicOperator = 'scalarlength';

interface UiComponentJsonLogicData {
  /**
   * All other fields collected on the current page.
   */
  form: FormValues | undefined;
  /**
   * Sugar provide var value in JSONLogic rules to refer to self.
   */
  value: FormValue | undefined;
  meta: {
    /**
     * Whether or not the UI is polling.
     *
     * NOTE: form inputs will not behave correctly if consuming this value (and it
     * doesn't make sense, anyways). The server is unable to recreate this value,
     * so it won't be used during validation to skip hidden inputs.
     */
    polling: boolean;
  };
}

/**
 * Custom Operators
 */
jsonLogic.add_operation('scalarlength', (a: string): number => a?.length || 0);

// WorkflowOperatorKey.Equals
jsonLogic.add_operation('=', (a: string, b: string) => {
  // disabling triple equals so that behavior is equivalent to default == operation
  // eslint-disable-next-line eqeqeq
  return a == b;
});

/**
 * We generally don't want bad JSONLogic to lead to crashes,
 * and instead fall back to undefined.
 *
 * null handling is for tsc strict checks and mirrors what jsonLogic.apply does
 * explicitly handle null case since RulesLogic type does not include null
 */
export const safeApply = <Ret = unknown>(
  rule: RulesLogic | null | undefined,
  data?: unknown,
  fallback?: Ret | undefined,
): Ret | undefined => {
  if (rule == null) return undefined;

  try {
    return jsonLogic.apply(rule, data);
  } catch (e) {
    if (e instanceof Error && e.message === 'Unrecognized operation matchesregex') {
      // matchesregex is not a supported client-side jsonlogic operation, and we don't want
      // to log this to sentry
      return fallback;
    }
    // If you came here from Sentry, this error fired due to a bad JSONLogic config.
    // Consider reaching out to the customer / customer team.
    captureException(e, 'warning', { rule });

    return fallback;
  }
};

/**
 * A hook that gives the component a JSONLogic applicator. The applicator can
 * reference other form values on the same step.
 */
export function useApply(field: string, formName: string) {
  const formValues = useSelector<RootState, FormValues | undefined>(
    (state) => state.form[formName]?.values,
  );
  const polling = useSelector<RootState, boolean>((state) => state.ui?.status === UiStatus.Blocked);

  const data: UiComponentJsonLogicData = useMemo(
    () => ({
      form: formValues,
      value: formValues?.[field],
      meta: {
        polling,
      },
    }),
    [formValues, field, polling],
  );

  /**
   * Wrapped version of `jsonLogic.apply` that supplies `data` automatically.
   */
  const apply: <Ret = unknown>(
    rule: RulesLogic | null | undefined,
    fallback?: Ret,
  ) => Ret | undefined = useCallback((rule, fallback) => safeApply(rule, data, fallback), [data]);

  return apply;
}

/**
 * A hook that takes all validation-related UI component properties, and
 * returns an array of Redux Form validation functions. Should be passed
 * to a field's `props.validate`.
 *
 * Note that all rules early exit if the field is hidden.
 */
export function useValidators({
  hidden,
  optional,
  validations,
}: {
  hidden?: RulesLogic | null;
  optional: UiComponentRule;
  validations?: UiComponentRule[] | null;
}): Rule[] {
  const validators = useMemo(() => {
    // We use the value and allValues passed by redux-form instead of using
    // the arguments for the hook to prevent recomputation. Recomputing
    // Redux Form validators is especially bad because it makes the field
    // unregister and reregister, which can cause unpredictable behavior.
    const optionalRule = (value: FormValue, form: FormValues | undefined) =>
      !safeApply<boolean>(hidden, { form, value }) &&
      // Treat false as empty for required purposes (e.g. for checkboxes)
      (isEmpty(value) || value === false) &&
      !safeApply<boolean>(optional.logic, { form, value }) &&
      optional.message;

    const validationRules = (validations ?? []).map(
      ({ logic, message }) =>
        (value: FormValue, form: FormValues | undefined) =>
          !safeApply<boolean>(hidden, { form, value }) &&
          // don't apply validations on empty optional inputs
          !(isEmpty(value) && safeApply<boolean>(optional.logic, { form, value })) &&
          !safeApply<boolean>(
            logic,
            {
              form,
              value,
            },
            // fallback to true for invalid (or with server-side only operators) validations
            // (effectively ignoring them)
            true,
          ) &&
          message,
    );

    return [optionalRule].concat(validationRules);
  }, [hidden, optional, validations]);
  return validators;
}
